import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import GradientButton from '../../components/buttons/GradientButton';
import ActionButton from '../../shared/components/action-button/ActionButton';
import useCompanyLocations from '../hooks/useCompanyLocations';
import useLocationAssignmentOverview from '../hooks/useLocationAssignmentOverview';
import LocationCoveragePanel from '../components/LocationCoveragePanel';
import { updateCompanyLocation } from '../locationRepository';
import { shouldShowLocationStep } from '../filterByLocation';
import { getLocationHoursSummary } from '../business-hours/locationBusinessHoursUtils';
import useLocationsSettingsTheme from './useLocationsSettingsTheme';
import {
  buildLocationCreatePath,
  buildLocationEditPath,
} from './locationSettingsPaths';
import { PageSkeleton } from '../../components/loading/skeletons';
import '../../components/settings/pages/BusinessInformation/BusinessInformation.css';
import './LocationsSettings.css';

const LocationsSettingsPage = () => {
  const navigate = useNavigate();
  const cssVars = useLocationsSettingsTheme();
  const { companyData } = useAuth();
  const { setNotification } = useNotification();
  const { locations, loading, reload } = useCompanyLocations({
    companyId: companyData?.id,
    companyData,
    autoMigrate: true,
  });
  const showCoverage = shouldShowLocationStep(locations);
  const ownerId = companyData?.ownerId || companyData?.createdBy;
  const {
    staff: assignmentStaff,
    services: assignmentServices,
    loading: assignmentLoading,
  } = useLocationAssignmentOverview({
    companyId: companyData?.id,
    ownerId,
    enabled: showCoverage,
  });
  const [saving, setSaving] = useState(false);

  const sortedLocations = useMemo(
    () => [...locations].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
    [locations]
  );

  const handleDeactivate = async (location) => {
    if (!companyData?.id || !location?.id) return;
    if (location.isDefault) {
      setNotification({
        open: true,
        message: 'Set another location as default before deactivating this one.',
        severity: 'warning',
      });
      return;
    }

    const confirmed = window.confirm(`Deactivate "${location.name}"? It will be hidden from booking.`);
    if (!confirmed) return;

    setSaving(true);
    try {
      await updateCompanyLocation(companyData.id, location.id, { active: false });
      await reload();
      setNotification({
        open: true,
        message: 'Location deactivated.',
        severity: 'success',
      });
    } catch (error) {
      console.error('Failed to deactivate location', error);
      setNotification({
        open: true,
        message: 'Failed to deactivate location.',
        severity: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="businessInfoPage locations-settings" style={cssVars}>
      <div className="businessInfoInner">
        <div className="businessInfoHeader">
          <div>
            <h1>Locations</h1>
            <p>
              Manage physical business locations, addresses, and hours for multi-site scheduling.
              {showCoverage ? ' Each card below shows who works there and which services can be booked.' : ''}
            </p>
          </div>
          <GradientButton
            variant="primary"
            onClick={() => navigate(buildLocationCreatePath())}
            disabled={saving}
          >
            Add location
          </GradientButton>
        </div>

        {loading ? (
          <PageSkeleton showHeader={false} variant="cards" rows={3} label="Loading locations" />
        ) : (
          <div className="locations-settings__list">
            {sortedLocations.map((location) => (
              <section key={location.id} className="biCard" aria-label={location.name}>
                <div className="biCardBody locations-settings__card-body">
                  <div className="locations-settings__card-main">
                    <div className="locations-settings__title-row">
                      <h2 className="locations-settings__name">{location.name}</h2>
                      <div className="locations-settings__badges">
                        {location.isDefault ? (
                          <span className="locations-settings__badge">Default</span>
                        ) : null}
                        {location.active === false ? (
                          <span className="locations-settings__badge locations-settings__badge--inactive">
                            Inactive
                          </span>
                        ) : null}
                      </div>
                    </div>
                    {location.address?.fullAddress ? (
                      <p className="biHelperText">{location.address.fullAddress}</p>
                    ) : null}
                    {location.phone ? (
                      <p className="biHelperText">{location.phone}</p>
                    ) : null}
                    <p className="biHelperText">{getLocationHoursSummary(location, companyData)}</p>
                    {showCoverage && location.active !== false ? (
                      assignmentLoading ? (
                        <p className="biHelperText">Loading team and service coverage…</p>
                      ) : (
                        <LocationCoveragePanel
                          location={location}
                          staff={assignmentStaff}
                          services={assignmentServices}
                        />
                      )
                    ) : null}
                  </div>
                  <div className="locations-settings__card-actions">
                    <ActionButton
                      type="button"
                      onClick={() => navigate(buildLocationEditPath(location.id))}
                      disabled={saving}
                    >
                      Edit
                    </ActionButton>
                    {location.active !== false && !location.isDefault ? (
                      <ActionButton type="button" onClick={() => handleDeactivate(location)} disabled={saving}>
                        Deactivate
                      </ActionButton>
                    ) : null}
                  </div>
                </div>
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationsSettingsPage;
